import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, ArrowLeft, Save, Send, Loader2, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useOrders } from '@/hooks/useOrders';
import { useVehicleTypes } from '@/hooks/useVehicleTypes';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { geocodeAddress } from '@/lib/geocoding';

const COUNTRY_CODES = [
  // Europe
  { value: '+33', label: '+33 (France)', flag: '🇫🇷' },
  { value: '+32', label: '+32 (Belgique)', flag: '🇧🇪' },
  { value: '+41', label: '+41 (Suisse)', flag: '🇨🇭' },
  { value: '+49', label: '+49 (Allemagne)', flag: '🇩🇪' },
  { value: '+34', label: '+34 (Espagne)', flag: '🇪🇸' },
  { value: '+39', label: '+39 (Italie)', flag: '🇮🇹' },
  { value: '+31', label: '+31 (Pays-Bas)', flag: '🇳🇱' },
  { value: '+44', label: '+44 (Royaume-Uni)', flag: '🇬🇧' },
  { value: '+351', label: '+351 (Portugal)', flag: '🇵🇹' },
  { value: '+352', label: '+352 (Luxembourg)', flag: '🇱🇺' },
  { value: '+43', label: '+43 (Autriche)', flag: '🇦🇹' },
  { value: '+45', label: '+45 (Danemark)', flag: '🇩🇰' },
  { value: '+46', label: '+46 (Suède)', flag: '🇸🇪' },
  { value: '+47', label: '+47 (Norvège)', flag: '🇳🇴' },
  { value: '+48', label: '+48 (Pologne)', flag: '🇵🇱' },
  { value: '+420', label: '+420 (Tchéquie)', flag: '🇨🇿' },
  { value: '+421', label: '+421 (Slovaquie)', flag: '🇸🇰' },
  { value: '+36', label: '+36 (Hongrie)', flag: '🇭🇺' },
  { value: '+40', label: '+40 (Roumanie)', flag: '🇷🇴' },
  { value: '+30', label: '+30 (Grèce)', flag: '🇬🇷' },
  { value: '+353', label: '+353 (Irlande)', flag: '🇮🇪' },
  { value: '+358', label: '+358 (Finlande)', flag: '🇫🇮' },
  
  // Amérique du Nord
  { value: '+1', label: '+1 (USA/Canada)', flag: '🇺🇸' },
  { value: '+52', label: '+52 (Mexique)', flag: '🇲🇽' },
  
  // Amérique du Sud
  { value: '+55', label: '+55 (Brésil)', flag: '🇧🇷' },
  { value: '+54', label: '+54 (Argentine)', flag: '🇦🇷' },
  { value: '+56', label: '+56 (Chili)', flag: '🇨🇱' },
  { value: '+57', label: '+57 (Colombie)', flag: '🇨🇴' },
  { value: '+58', label: '+58 (Venezuela)', flag: '🇻🇪' },
  
  // Asie
  { value: '+86', label: '+86 (Chine)', flag: '🇨🇳' },
  { value: '+81', label: '+81 (Japon)', flag: '🇯🇵' },
  { value: '+82', label: '+82 (Corée du Sud)', flag: '🇰🇷' },
  { value: '+91', label: '+91 (Inde)', flag: '🇮🇳' },
  { value: '+65', label: '+65 (Singapour)', flag: '🇸🇬' },
  { value: '+60', label: '+60 (Malaisie)', flag: '🇲🇾' },
  { value: '+66', label: '+66 (Thaïlande)', flag: '🇹🇭' },
  { value: '+84', label: '+84 (Vietnam)', flag: '🇻🇳' },
  { value: '+62', label: '+62 (Indonésie)', flag: '🇮🇩' },
  { value: '+63', label: '+63 (Philippines)', flag: '🇵🇭' },
  
  // Moyen-Orient
  { value: '+971', label: '+971 (Émirats)', flag: '🇦🇪' },
  { value: '+966', label: '+966 (Arabie Saoudite)', flag: '🇸🇦' },
  { value: '+972', label: '+972 (Israël)', flag: '🇮🇱' },
  { value: '+90', label: '+90 (Turquie)', flag: '🇹🇷' },
  
  // Afrique
  { value: '+27', label: '+27 (Afrique du Sud)', flag: '🇿🇦' },
  { value: '+212', label: '+212 (Maroc)', flag: '🇲🇦' },
  { value: '+213', label: '+213 (Algérie)', flag: '🇩🇿' },
  { value: '+216', label: '+216 (Tunisie)', flag: '🇹🇳' },
  { value: '+234', label: '+234 (Nigeria)', flag: '🇳🇬' },
  { value: '+254', label: '+254 (Kenya)', flag: '🇰🇪' },
  
  // Océanie
  { value: '+61', label: '+61 (Australie)', flag: '🇦🇺' },
  { value: '+64', label: '+64 (Nouvelle-Zélande)', flag: '🇳🇿' },
];

const createOrderSchema = (t: any) => z.object({
  // Itinéraire - Départ
  pickup_street_number: z.string().trim().max(10, t('createOrder.validation.numberTooLong')).optional(),
  pickup_street_name: z.string().trim().min(3, t('createOrder.validation.streetNameRequired')).max(200, t('createOrder.validation.streetNameTooLong')),
  pickup_postal_code: z.string().trim().min(4, t('createOrder.validation.postalCodeRequired')).max(10, t('createOrder.validation.postalCodeTooLong')),
  pickup_city: z.string().trim().min(2, t('createOrder.validation.cityRequired')).max(100, t('createOrder.validation.cityTooLong')),
  pickup_country: z.string().trim().min(2, t('createOrder.validation.countryRequired')).max(100, t('createOrder.validation.countryTooLong')),
  pickup_date: z.date({ required_error: t('createOrder.validation.pickupDateRequired') }),
  pickup_time: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, t('createOrder.validation.invalidTime')),
  pickup_contact: z.string().trim().min(2, t('createOrder.validation.contactRequired')).max(100, t('createOrder.validation.contactTooLong')),
  pickup_phone: z.string().trim().min(8, t('createOrder.validation.phoneRequired')).max(20, t('createOrder.validation.phoneTooLong')),
  start_details: z.string().trim().max(1000, t('createOrder.validation.detailsTooLong')).optional(),
  
  // Itinéraire - Livraison
  delivery_street_number: z.string().trim().max(10, t('createOrder.validation.numberTooLong')).optional(),
  delivery_street_name: z.string().trim().min(3, t('createOrder.validation.streetNameRequired')).max(200, t('createOrder.validation.streetNameTooLong')),
  delivery_postal_code: z.string().trim().min(4, t('createOrder.validation.postalCodeRequired')).max(10, t('createOrder.validation.postalCodeTooLong')),
  delivery_city: z.string().trim().min(2, t('createOrder.validation.cityRequired')).max(100, t('createOrder.validation.cityTooLong')),
  delivery_country: z.string().trim().min(2, t('createOrder.validation.countryRequired')).max(100, t('createOrder.validation.countryTooLong')),
  delivery_date: z.date({ required_error: t('createOrder.validation.deliveryDateRequired') }),
  delivery_time: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, t('createOrder.validation.invalidTime')),
  delivery_contact: z.string().trim().min(2, t('createOrder.validation.contactRequired')).max(100, t('createOrder.validation.contactTooLong')),
  delivery_phone: z.string().trim().min(8, t('createOrder.validation.phoneRequired')).max(20, t('createOrder.validation.phoneTooLong')),
  delivery_details: z.string().trim().max(1000, t('createOrder.validation.detailsTooLong')).optional(),
  
  // Marchandise
  cargo_title: z.string().trim().min(3, t('createOrder.validation.cargoTitleRequired')).max(200, t('createOrder.validation.cargoTitleTooLong')),
  trailer_type: z.string().min(1, t('createOrder.validation.vehicleTypeRequired')),
  weight_kg: z.coerce.number().positive(t('createOrder.validation.weightRequired')).max(100000, t('createOrder.validation.weightMax')),
  volume_m3: z.coerce.number().positive().max(200, t('createOrder.validation.volumeMax')).optional().or(z.literal('')),
  cargo_unit_type: z.enum(['palettes', 'caisses', 'euro-palettes']).optional(),
  pallets_count: z.coerce.number().int().nonnegative(t('createOrder.validation.quantityNegative')).max(100, t('createOrder.validation.quantityMax')).optional().or(z.literal('')),
  cargo_description: z.string().trim().min(10, t('createOrder.validation.descriptionRequired')).max(1000, t('createOrder.validation.descriptionTooLong')),
  special_instructions: z.string().trim().max(1000, t('createOrder.validation.instructionsTooLong')).optional(),
  
  // Tarification
  pricing_mode: z.enum(['fixed', 'auction']),
  proposed_price: z.coerce.number().positive(t('createOrder.validation.priceRequired')).max(1000000, t('createOrder.validation.priceMax')).optional().or(z.literal('')),
  marketplace_visible: z.boolean(),
});

const CreateOrder = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { createOrder } = useOrders();
  const { vehicleTypes, isLoading: loadingVehicles, getVehicleTypeName } = useVehicleTypes();
  
  const orderSchema = createOrderSchema(t);
  type OrderFormData = z.infer<typeof orderSchema>;
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pickupCountryCode, setPickupCountryCode] = useState('+33');
  const [deliveryCountryCode, setDeliveryCountryCode] = useState('+33');
  const [startImages, setStartImages] = useState<File[]>([]);
  const [deliveryImages, setDeliveryImages] = useState<File[]>([]);
  const [cargoImage, setCargoImage] = useState<File | null>(null);
  
  // États pour les coordonnées GPS
  const [pickupCoords, setPickupCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [deliveryCoords, setDeliveryCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [isGeocodingPickup, setIsGeocodingPickup] = useState(false);
  const [isGeocodingDelivery, setIsGeocodingDelivery] = useState(false);

  const form = useForm<OrderFormData>({
    resolver: zodResolver(orderSchema),
    defaultValues: {
      pricing_mode: 'fixed',
      marketplace_visible: false,
      pickup_country: 'France',
      delivery_country: 'France',
      pickup_time: '08:00',
      delivery_time: '17:00',
    },
  });

  // Pré-remplir le contact de départ avec le nom de l'utilisateur
  useEffect(() => {
    if (user?.name) {
      form.setValue('pickup_contact', user.name);
    }
  }, [user, form]);

  // Géocodage automatique de l'adresse de pickup
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name?.startsWith('pickup_')) {
        const timer = setTimeout(async () => {
          const { pickup_street_name, pickup_postal_code, pickup_city, pickup_country } = value;
          
          if (pickup_street_name && pickup_postal_code && pickup_city && pickup_country) {
            const address = [
              value.pickup_street_number,
              pickup_street_name,
              pickup_postal_code,
              pickup_city,
              pickup_country
            ].filter(Boolean).join(', ');
            
            setIsGeocodingPickup(true);
            const result = await geocodeAddress(address);
            setIsGeocodingPickup(false);
            
            if ('latitude' in result) {
              setPickupCoords({ lat: result.latitude, lng: result.longitude });
            } else {
              setPickupCoords(null);
            }
          }
        }, 1000);
        
        return () => clearTimeout(timer);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form]);

  // Géocodage automatique de l'adresse de delivery
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name?.startsWith('delivery_')) {
        const timer = setTimeout(async () => {
          const { delivery_street_name, delivery_postal_code, delivery_city, delivery_country } = value;
          
          if (delivery_street_name && delivery_postal_code && delivery_city && delivery_country) {
            const address = [
              value.delivery_street_number,
              delivery_street_name,
              delivery_postal_code,
              delivery_city,
              delivery_country
            ].filter(Boolean).join(', ');
            
            setIsGeocodingDelivery(true);
            const result = await geocodeAddress(address);
            setIsGeocodingDelivery(false);
            
            if ('latitude' in result) {
              setDeliveryCoords({ lat: result.latitude, lng: result.longitude });
            } else {
              setDeliveryCoords(null);
            }
          }
        }, 1000);
        
        return () => clearTimeout(timer);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form]);

  const pricingMode = form.watch('pricing_mode');

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (startImages.length + files.length > 5) {
      toast.error(t('createOrder.access.maxImagesError'));
      return;
    }
    setStartImages((prev) => [...prev, ...files]);
  };

  const handleDeliveryImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (deliveryImages.length + files.length > 5) {
      toast.error(t('createOrder.access.maxImagesError'));
      return;
    }
    setDeliveryImages((prev) => [...prev, ...files]);
  };

  const removeImage = (index: number) => {
    setStartImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeDeliveryImage = (index: number) => {
    setDeliveryImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCargoImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCargoImage(file);
    }
  };

  const removeCargoImage = () => {
    setCargoImage(null);
  };

  const onSubmit = async (data: OrderFormData, status: 'draft' | 'posted') => {
    setIsSubmitting(true);
    try {
      // Validation conditionnelle du prix fixe
      if (status === 'posted' && data.pricing_mode === 'fixed' && !data.proposed_price) {
        toast.error(t('createOrder.toasts.priceRequired'));
        setIsSubmitting(false);
        return;
      }

      // Construire l'adresse complète à partir des champs séparés
      const pickupAddress = [
        data.pickup_street_number,
        data.pickup_street_name,
        data.pickup_postal_code,
        data.pickup_city,
        data.pickup_country
      ].filter(Boolean).join(', ');

      const deliveryAddress = [
        data.delivery_street_number,
        data.delivery_street_name,
        data.delivery_postal_code,
        data.delivery_city,
        data.delivery_country
      ].filter(Boolean).join(', ');

      // Combiner date et heure pour pickup
      const pickupDateTime = new Date(data.pickup_date);
      const [pickupHours, pickupMinutes] = data.pickup_time.split(':').map(Number);
      pickupDateTime.setHours(pickupHours, pickupMinutes, 0, 0);

      // Combiner date et heure pour delivery
      const deliveryDateTime = new Date(data.delivery_date);
      const [deliveryHours, deliveryMinutes] = data.delivery_time.split(':').map(Number);
      deliveryDateTime.setHours(deliveryHours, deliveryMinutes, 0, 0);

      const orderResult = await createOrder({
        pickup_address: pickupAddress,
        pickup_street_number: data.pickup_street_number,
        pickup_street_name: data.pickup_street_name,
        pickup_postal_code: data.pickup_postal_code,
        pickup_city: data.pickup_city,
        pickup_country: data.pickup_country,
        pickup_lat: pickupCoords?.lat,
        pickup_lng: pickupCoords?.lng,
        pickup_date: pickupDateTime.toISOString(),
        pickup_contact: data.pickup_contact,
        pickup_phone: `${pickupCountryCode} ${data.pickup_phone}`,
        start_details: data.start_details,
        delivery_address: deliveryAddress,
        delivery_street_number: data.delivery_street_number,
        delivery_street_name: data.delivery_street_name,
        delivery_postal_code: data.delivery_postal_code,
        delivery_city: data.delivery_city,
        delivery_country: data.delivery_country,
        delivery_lat: deliveryCoords?.lat,
        delivery_lng: deliveryCoords?.lng,
        delivery_date: deliveryDateTime.toISOString(),
        delivery_contact: data.delivery_contact,
        delivery_phone: data.delivery_phone,
        delivery_details: data.delivery_details,
        cargo_title: data.cargo_title,
        trailer_type: data.trailer_type,
        weight_kg: data.weight_kg,
        volume_m3: data.volume_m3 ? Number(data.volume_m3) : undefined,
        pallets_count: data.pallets_count ? Number(data.pallets_count) : undefined,
        cargo_description: data.cargo_description,
        special_instructions: data.special_instructions,
        proposed_price: data.proposed_price ? Number(data.proposed_price) : undefined,
        marketplace_visible: data.marketplace_visible,
        status,
      });

      // Upload images if any
      if ((startImages.length > 0 || deliveryImages.length > 0 || cargoImage) && orderResult) {
        const startPaths: string[] = [];
        const deliveryPaths: string[] = [];
        let cargoPicturePath: string | null = null;

        // Upload start images
        if (startImages.length > 0) {
          const startPromises = startImages.map(async (file) => {
            const fileExt = file.name.split('.').pop();
            const fileName = `${orderResult.id}/start/${Math.random()}.${fileExt}`;
            
            const { error: uploadError } = await supabase.storage
              .from('order-images')
              .upload(fileName, file);

            if (uploadError) throw uploadError;
            return fileName;
          });
          startPaths.push(...await Promise.all(startPromises));
        }

        // Upload delivery images
        if (deliveryImages.length > 0) {
          const deliveryPromises = deliveryImages.map(async (file) => {
            const fileExt = file.name.split('.').pop();
            const fileName = `${orderResult.id}/delivery/${Math.random()}.${fileExt}`;
            
            const { error: uploadError } = await supabase.storage
              .from('order-images')
              .upload(fileName, file);

            if (uploadError) throw uploadError;
            return fileName;
          });
          deliveryPaths.push(...await Promise.all(deliveryPromises));
        }

        // Upload cargo image
        if (cargoImage) {
          const fileExt = cargoImage.name.split('.').pop();
          const fileName = `${orderResult.id}/cargo/${Math.random()}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('order-images')
            .upload(fileName, cargoImage);

          if (uploadError) throw uploadError;
          cargoPicturePath = fileName;
        }
        
        // Update order with image paths
        await supabase
          .from('orders')
          .update({ 
            start_pictures: startPaths.length > 0 ? startPaths : null,
            delivery_pictures: deliveryPaths.length > 0 ? deliveryPaths : null,
            cargo_picture: cargoPicturePath
          })
          .eq('id', orderResult.id);
      }

      toast.success(status === 'draft' ? t('createOrder.toasts.draftSaved') : t('createOrder.toasts.orderCreated'));
      navigate('/shipper/dashboard');
    } catch (error: any) {
      console.error('Error creating order:', error);
      toast.error(error.message || t('createOrder.toasts.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/shipper/dashboard')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-3xl font-medium text-secondary font-heading">
            {t('createOrder.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('createOrder.subtitle')}
          </p>
        </div>
      </div>

      <form className="space-y-6">
        {/* Section 1: Itinéraire */}
        <Card className="rounded-xl">
          <CardHeader>
            <CardTitle className="text-xl font-medium text-secondary font-heading">
              {t('createOrder.section1.title')}
            </CardTitle>
            <CardDescription>
              {t('createOrder.section1.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Pickup */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground uppercase tracking-wide">{t('createOrder.section1.pickup')}</h3>
              
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="pickup_street_number" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.address.streetNumber')}
                  </Label>
                  <Input
                    id="pickup_street_number"
                    placeholder={t('createOrder.address.streetNumberPlaceholder')}
                    {...form.register('pickup_street_number')}
                  />
                  {form.formState.errors.pickup_street_number && (
                    <p className="text-xs text-destructive">{form.formState.errors.pickup_street_number.message}</p>
                  )}
                </div>

                <div className="col-span-3 space-y-2">
                  <Label htmlFor="pickup_street_name" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.address.streetName')} *
                  </Label>
                  <Input
                    id="pickup_street_name"
                    placeholder={t('createOrder.address.streetNamePlaceholder')}
                    {...form.register('pickup_street_name')}
                  />
                  {form.formState.errors.pickup_street_name && (
                    <p className="text-xs text-destructive">{form.formState.errors.pickup_street_name.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="pickup_postal_code" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.address.postalCode')} *
                  </Label>
                  <Input
                    id="pickup_postal_code"
                    placeholder={t('createOrder.address.postalCodePlaceholder')}
                    {...form.register('pickup_postal_code')}
                  />
                  {form.formState.errors.pickup_postal_code && (
                    <p className="text-xs text-destructive">{form.formState.errors.pickup_postal_code.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pickup_city" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.address.city')} *
                  </Label>
                  <Input
                    id="pickup_city"
                    placeholder={t('createOrder.address.cityPlaceholder')}
                    {...form.register('pickup_city')}
                  />
                  {form.formState.errors.pickup_city && (
                    <p className="text-xs text-destructive">{form.formState.errors.pickup_city.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pickup_country" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.address.country')} *
                  </Label>
                  <Input
                    id="pickup_country"
                    placeholder={t('createOrder.address.countryPlaceholder')}
                    {...form.register('pickup_country')}
                  />
                  {form.formState.errors.pickup_country && (
                    <p className="text-xs text-destructive">{form.formState.errors.pickup_country.message}</p>
                  )}
                </div>
              </div>

              {/* Indicateur de géocodage pickup */}
              {(isGeocodingPickup || pickupCoords) && (
                <div className="flex items-center gap-2 text-sm">
                  {isGeocodingPickup ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-accent" />
                      <span className="text-muted-foreground">{t('createOrder.gps.calculating')}</span>
                    </>
                  ) : pickupCoords ? (
                    <>
                      <MapPin className="w-4 h-4 text-success" />
                      <span className="text-success">{t('createOrder.gps.calculated')}</span>
                    </>
                  ) : null}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.dateTime.pickupDate')} *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !form.watch('pickup_date') && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.watch('pickup_date') ? (
                          format(form.watch('pickup_date'), 'PPP', { locale: fr })
                        ) : (
                          <span>{t('createOrder.dateTime.chooseDate')}</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.watch('pickup_date')}
                        onSelect={(date) => form.setValue('pickup_date', date as Date)}
                        disabled={(date) => date < new Date()}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  {form.formState.errors.pickup_date && (
                    <p className="text-xs text-destructive">{form.formState.errors.pickup_date.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pickup_time" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.dateTime.pickupTime')} *
                  </Label>
                  <Input
                    id="pickup_time"
                    type="time"
                    {...form.register('pickup_time')}
                  />
                  {form.formState.errors.pickup_time && (
                    <p className="text-xs text-destructive">{form.formState.errors.pickup_time.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="pickup_contact" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.contact.label')} *
                  </Label>
                  <Input
                    id="pickup_contact"
                    placeholder={t('createOrder.contact.contactPlaceholder')}
                    {...form.register('pickup_contact')}
                  />
                  {form.formState.errors.pickup_contact && (
                    <p className="text-xs text-destructive">{form.formState.errors.pickup_contact.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pickup_phone" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.contact.phone')} *
                  </Label>
                  <div className="flex gap-2">
                    <Select value={pickupCountryCode} onValueChange={setPickupCountryCode}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRY_CODES.map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.flag} {country.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="pickup_phone"
                      placeholder={t('createOrder.contact.phonePlaceholder')}
                      {...form.register('pickup_phone')}
                      className="flex-1"
                    />
                  </div>
                  {form.formState.errors.pickup_phone && (
                    <p className="text-xs text-destructive">{form.formState.errors.pickup_phone.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="start_details" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('createOrder.access.detailsLabel')}
                </Label>
                <Textarea
                  id="start_details"
                  placeholder={t('createOrder.access.detailsPlaceholder')}
                  rows={3}
                  {...form.register('start_details')}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  {t('createOrder.access.detailsHelper')}
                </p>
                {form.formState.errors.start_details && (
                  <p className="text-xs text-destructive">{form.formState.errors.start_details.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('createOrder.access.photosLabel')}
                </Label>
                <div className="space-y-3">
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('createOrder.access.photosHelper')}
                  </p>
                  
                  {startImages.length > 0 && (
                    <div className="grid grid-cols-3 gap-3">
                      {startImages.map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border border-border-light"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* Delivery */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground uppercase tracking-wide">{t('createOrder.section1.delivery')}</h3>
              
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="delivery_street_number" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.address.streetNumber')}
                  </Label>
                  <Input
                    id="delivery_street_number"
                    placeholder={t('createOrder.address.streetNumberPlaceholder')}
                    {...form.register('delivery_street_number')}
                  />
                  {form.formState.errors.delivery_street_number && (
                    <p className="text-xs text-destructive">{form.formState.errors.delivery_street_number.message}</p>
                  )}
                </div>

                <div className="col-span-3 space-y-2">
                  <Label htmlFor="delivery_street_name" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.address.streetName')} *
                  </Label>
                  <Input
                    id="delivery_street_name"
                    placeholder={t('createOrder.address.streetNamePlaceholder')}
                    {...form.register('delivery_street_name')}
                  />
                  {form.formState.errors.delivery_street_name && (
                    <p className="text-xs text-destructive">{form.formState.errors.delivery_street_name.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="delivery_postal_code" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.address.postalCode')} *
                  </Label>
                  <Input
                    id="delivery_postal_code"
                    placeholder={t('createOrder.address.postalCodePlaceholder')}
                    {...form.register('delivery_postal_code')}
                  />
                  {form.formState.errors.delivery_postal_code && (
                    <p className="text-xs text-destructive">{form.formState.errors.delivery_postal_code.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="delivery_city" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.address.city')} *
                  </Label>
                  <Input
                    id="delivery_city"
                    placeholder={t('createOrder.address.cityPlaceholder')}
                    {...form.register('delivery_city')}
                  />
                  {form.formState.errors.delivery_city && (
                    <p className="text-xs text-destructive">{form.formState.errors.delivery_city.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="delivery_country" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.address.country')} *
                  </Label>
                  <Input
                    id="delivery_country"
                    placeholder={t('createOrder.address.countryPlaceholder')}
                    {...form.register('delivery_country')}
                  />
                  {form.formState.errors.delivery_country && (
                    <p className="text-xs text-destructive">{form.formState.errors.delivery_country.message}</p>
                  )}
                </div>
              </div>

              {/* Indicateur de géocodage delivery */}
              {(isGeocodingDelivery || deliveryCoords) && (
                <div className="flex items-center gap-2 text-sm">
                  {isGeocodingDelivery ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-accent" />
                      <span className="text-muted-foreground">{t('createOrder.gps.calculating')}</span>
                    </>
                  ) : deliveryCoords ? (
                    <>
                      <MapPin className="w-4 h-4 text-success" />
                      <span className="text-success">{t('createOrder.gps.calculated')}</span>
                    </>
                  ) : null}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.dateTime.deliveryDate')} *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !form.watch('delivery_date') && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.watch('delivery_date') ? (
                          format(form.watch('delivery_date'), 'PPP', { locale: fr })
                        ) : (
                          <span>{t('createOrder.dateTime.chooseDate')}</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.watch('delivery_date')}
                        onSelect={(date) => form.setValue('delivery_date', date as Date)}
                        disabled={(date) => date < new Date()}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  {form.formState.errors.delivery_date && (
                    <p className="text-xs text-destructive">{form.formState.errors.delivery_date.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="delivery_time" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.dateTime.deliveryTime')} *
                  </Label>
                  <Input
                    id="delivery_time"
                    type="time"
                    {...form.register('delivery_time')}
                  />
                  {form.formState.errors.delivery_time && (
                    <p className="text-xs text-destructive">{form.formState.errors.delivery_time.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="delivery_contact" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.contact.label')} *
                  </Label>
                  <Input
                    id="delivery_contact"
                    placeholder={t('createOrder.contact.contactPlaceholder')}
                    {...form.register('delivery_contact')}
                  />
                  {form.formState.errors.delivery_contact && (
                    <p className="text-xs text-destructive">{form.formState.errors.delivery_contact.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="delivery_phone" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.contact.phone')} *
                  </Label>
                  <div className="flex gap-2">
                    <Select value={deliveryCountryCode} onValueChange={setDeliveryCountryCode}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRY_CODES.map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.flag} {country.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="delivery_phone"
                      placeholder={t('createOrder.contact.phonePlaceholder')}
                      {...form.register('delivery_phone')}
                      className="flex-1"
                    />
                  </div>
                  {form.formState.errors.delivery_phone && (
                    <p className="text-xs text-destructive">{form.formState.errors.delivery_phone.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="delivery_details" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('createOrder.access.detailsLabel')}
                </Label>
                <Textarea
                  id="delivery_details"
                  placeholder={t('createOrder.access.deliveryDetailsPlaceholder')}
                  rows={3}
                  {...form.register('delivery_details')}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  {t('createOrder.access.deliveryDetailsHelper')}
                </p>
                {form.formState.errors.delivery_details && (
                  <p className="text-xs text-destructive">{form.formState.errors.delivery_details.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('createOrder.access.photosLabel')}
                </Label>
                <div className="space-y-3">
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleDeliveryImageUpload}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('createOrder.access.deliveryPhotosHelper')}
                  </p>
                  
                  {deliveryImages.length > 0 && (
                    <div className="grid grid-cols-3 gap-3">
                      {deliveryImages.map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border border-border-light"
                          />
                          <button
                            type="button"
                            onClick={() => removeDeliveryImage(index)}
                            className="absolute top-1 right-1 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Marchandise */}
        <Card className="rounded-xl">
          <CardHeader>
            <CardTitle className="text-xl font-medium text-secondary font-heading">
              {t('createOrder.section2.title')}
            </CardTitle>
            <CardDescription>
              {t('createOrder.section2.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cargo_title" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('createOrder.section2.cargoTitle')} *
              </Label>
              <Input
                id="cargo_title"
                placeholder={t('createOrder.section2.cargoTitlePlaceholder')}
                {...form.register('cargo_title')}
              />
              {form.formState.errors.cargo_title && (
                <p className="text-xs text-destructive">{form.formState.errors.cargo_title.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="trailer_type" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('createOrder.section2.vehicleType')} *
              </Label>
              <Select
                value={form.watch('trailer_type')}
                onValueChange={(value) => form.setValue('trailer_type', value)}
                disabled={loadingVehicles}
              >
                <SelectTrigger id="trailer_type">
                  <SelectValue placeholder={t('createOrder.section2.vehicleTypePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">{t('createOrder.section2.anyVehicle')}</SelectItem>
                  {vehicleTypes.map((vt) => (
                    <SelectItem key={vt.id} value={vt.trailer_type}>
                      {getVehicleTypeName(vt.trailer_type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.trailer_type && (
                <p className="text-xs text-destructive">{form.formState.errors.trailer_type.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="weight_kg" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('createOrder.section2.weight')} *
                </Label>
                <Input
                  id="weight_kg"
                  type="number"
                  placeholder={t('createOrder.section2.weightPlaceholder')}
                  {...form.register('weight_kg')}
                />
                {form.formState.errors.weight_kg && (
                  <p className="text-xs text-destructive">{form.formState.errors.weight_kg.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="volume_m3" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('createOrder.section2.volume')}
                </Label>
                <Input
                  id="volume_m3"
                  type="number"
                  step="0.1"
                  placeholder={t('createOrder.section2.volumePlaceholder')}
                  {...form.register('volume_m3')}
                />
                {form.formState.errors.volume_m3 && (
                  <p className="text-xs text-destructive">{form.formState.errors.volume_m3.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">

              <div className="space-y-2">
                <Label htmlFor="cargo_unit_type" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('createOrder.section2.unitType')}
                </Label>
                <Select
                  value={form.watch('cargo_unit_type')}
                  onValueChange={(value) => form.setValue('cargo_unit_type', value as 'palettes' | 'caisses' | 'euro-palettes')}
                >
                  <SelectTrigger id="cargo_unit_type">
                    <SelectValue placeholder={t('createOrder.section2.unitTypePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-50">
                    <SelectItem value="palettes">{t('createOrder.unitTypes.palettes')}</SelectItem>
                    <SelectItem value="caisses">{t('createOrder.unitTypes.caisses')}</SelectItem>
                    <SelectItem value="euro-palettes">{t('createOrder.unitTypes.euroPalettes')}</SelectItem>
                  </SelectContent>
                </Select>
                {form.formState.errors.cargo_unit_type && (
                  <p className="text-xs text-destructive">{form.formState.errors.cargo_unit_type.message}</p>
                )}
              </div>

              {form.watch('cargo_unit_type') && (
                <div className="space-y-2">
                  <Label htmlFor="pallets_count" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {t('createOrder.section2.quantity')}
                  </Label>
                  <Input
                    id="pallets_count"
                    type="number"
                    placeholder={t('createOrder.section2.quantityPlaceholder')}
                    {...form.register('pallets_count')}
                  />
                  {form.formState.errors.pallets_count && (
                    <p className="text-xs text-destructive">{form.formState.errors.pallets_count.message}</p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cargo_description" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('createOrder.section2.cargoDescription')} *
              </Label>
              <Textarea
                id="cargo_description"
                placeholder={t('createOrder.section2.cargoDescriptionPlaceholder')}
                rows={3}
                {...form.register('cargo_description')}
              />
              {form.formState.errors.cargo_description && (
                <p className="text-xs text-destructive">{form.formState.errors.cargo_description.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="special_instructions" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('createOrder.section2.specialInstructions')}
              </Label>
              <Textarea
                id="special_instructions"
                placeholder={t('createOrder.section2.specialInstructionsPlaceholder')}
                rows={2}
                {...form.register('special_instructions')}
              />
              {form.formState.errors.special_instructions && (
                <p className="text-xs text-destructive">{form.formState.errors.special_instructions.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('createOrder.section2.cargoPhoto')}
              </Label>
              <div className="space-y-3">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleCargoImageUpload}
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground">
                  {t('createOrder.section2.cargoPhotoHelper')}
                </p>
                
                {cargoImage && (
                  <div className="relative w-48 group">
                    <img
                      src={URL.createObjectURL(cargoImage)}
                      alt="Cargo preview"
                      className="w-full h-32 object-cover rounded-lg border border-border-light"
                    />
                    <button
                      type="button"
                      onClick={removeCargoImage}
                      className="absolute top-1 right-1 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Tarification */}
        <Card className="rounded-xl">
          <CardHeader>
            <CardTitle className="text-xl font-medium text-secondary font-heading">
              {t('createOrder.section3.title')}
            </CardTitle>
            <CardDescription>
              {t('createOrder.section3.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('createOrder.section3.pricingMode')} *
              </Label>
              <RadioGroup
                value={pricingMode}
                onValueChange={(value: 'fixed' | 'auction') => form.setValue('pricing_mode', value)}
              >
                <div className="flex items-center space-x-2 p-3 border border-border rounded-lg hover:bg-muted transition-colors">
                  <RadioGroupItem value="fixed" id="fixed" />
                  <Label htmlFor="fixed" className="flex-1 cursor-pointer">
                    <div className="font-medium text-foreground">{t('createOrder.section3.fixedPrice')}</div>
                    <div className="text-xs text-muted-foreground">{t('createOrder.section3.fixedPriceDesc')}</div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border border-border rounded-lg hover:bg-muted transition-colors">
                  <RadioGroupItem value="auction" id="auction" />
                  <Label htmlFor="auction" className="flex-1 cursor-pointer">
                    <div className="font-medium text-foreground">{t('createOrder.section3.auction')}</div>
                    <div className="text-xs text-muted-foreground">{t('createOrder.section3.auctionDesc')}</div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {pricingMode === 'fixed' && (
              <div className="space-y-2">
                <Label htmlFor="proposed_price" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('createOrder.section3.proposedPrice')} *
                </Label>
                <Input
                  id="proposed_price"
                  type="number"
                  step="0.01"
                  placeholder={t('createOrder.section3.proposedPricePlaceholder')}
                  {...form.register('proposed_price')}
                />
                {form.formState.errors.proposed_price && (
                  <p className="text-xs text-destructive">{form.formState.errors.proposed_price.message}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 4: Visibilité (Optional - for marketplace) */}
        <Card className="rounded-xl">
          <CardHeader>
            <CardTitle className="text-xl font-medium text-secondary font-heading">
              {t('createOrder.section4.title')}
            </CardTitle>
            <CardDescription>
              {t('createOrder.section4.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="marketplace_visible" className="text-base font-medium text-foreground cursor-pointer">
                  {t('createOrder.section4.marketplaceVisible')}
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('createOrder.section4.marketplaceVisibleDesc')}
                </p>
              </div>
              <Switch
                id="marketplace_visible"
                checked={form.watch('marketplace_visible')}
                onCheckedChange={(checked) => form.setValue('marketplace_visible', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={form.handleSubmit((data) => onSubmit(data, 'draft'))}
            disabled={isSubmitting}
            className="flex-1 gap-2"
          >
            <Save size={18} />
            {t('createOrder.actions.saveDraft')}
          </Button>
          <Button
            type="button"
            size="lg"
            onClick={form.handleSubmit((data) => onSubmit(data, 'posted'))}
            disabled={isSubmitting}
            className="flex-1 gap-2"
          >
            <Send size={18} />
            {isSubmitting ? t('createOrder.actions.creating') : t('createOrder.actions.createOrder')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateOrder;
